import {
  CAccordion,
  CAccordionBody,
  CAccordionHeader,
  CAccordionItem,
  CButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CTable,
  CTableBody,
  CTableHeaderCell,
  CTableRow,
  CTooltip,
} from '@coreui/react'
import SearchIcon from '@mui/icons-material/Search'
import { ArchiveRestore } from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'
import Pageloader from '../../components/shared/Pageloader.jsx'
import SavePatients from '../../services/general/savePatientinformation.api'
import toast from 'react-hot-toast'
import { toastConfig } from '../../constants/other.config.js'
import { patientMatchesSearch } from '../../utils/searchUtils'
import './Patient.css'
import './dashboard.css'

const parseApiData = (response, fallback = []) => {
  try {
     console.log('parseApiData response:', JSON.parse(response?.d)?.Data)
    return JSON.parse(response?.d)?.Data ?? fallback
  } catch (error) {
    console.error('Error parsing API data:', error)
    return fallback
  }
}

const isArchivedPatient = (patient) => {
  const archiveValue = patient?.isArchive ?? patient?.IsArchive
  return archiveValue === true || archiveValue === 1 || String(archiveValue).toLowerCase() === 'true'
}

const getPatientName = (patient) =>
  [patient?.FirstName, patient?.MiddleName, patient?.LastName]
    .filter(Boolean)
    .join(' ')
    .trim()

const Archive = () => {
  const [patientTheme, setPatientTheme] = useState(() => localStorage.getItem('provio_theme') || 'dark')
  const [archivedPatients, setArchivedPatients] = useState([])
  const [searchValue, setSearchValue] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [unarchivePatient, setUnarchivePatient] = useState(null)
  const [isUnarchiving, setIsUnarchiving] = useState(false)

  useEffect(() => {
    const handleThemeChange = () => {
      setPatientTheme(localStorage.getItem('provio_theme') || 'dark')
    }

    window.addEventListener('storage', handleThemeChange)
    window.addEventListener('provio-theme-change', handleThemeChange)

    return () => {
      window.removeEventListener('storage', handleThemeChange)
      window.removeEventListener('provio-theme-change', handleThemeChange)
    }
  }, [])

  useEffect(() => {
    let isMounted = true

    const loadArchivedPatients = async () => {
      setIsLoading(true)
      setLoadError(false)

      try {
        const patientData = await SavePatients('getarchive', '', '', '')
      
        const patients = parseApiData(patientData, [])
           console.log('Archived patients data:', patientData , patients)

        if (isMounted) {
          setArchivedPatients(patients)
        }
      } catch (error) {
        if (isMounted) {
          setArchivedPatients([])
          setLoadError(true)
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    loadArchivedPatients()

    return () => {
      isMounted = false
    }
  }, [])

  const filteredPatients = useMemo(() => {
    return archivedPatients
      .filter((patient) =>
        patientMatchesSearch(patient, searchValue, {
          facilityName: patient?.FacilityName,
        }),
      )
      .sort((patientA, patientB) =>
        getPatientName(patientA).localeCompare(getPatientName(patientB), undefined, {
          sensitivity: 'base',
        }),
      )
  }, [archivedPatients, searchValue])

  const groupedPatients = useMemo(() => {
    return filteredPatients.reduce((groups, patient) => {
      const facilityName = String(patient?.FacilityName || '').trim() || 'Unknown Facility'

      if (!groups[facilityName]) {
        groups[facilityName] = []
      }

      groups[facilityName].push(patient)
      return groups
    }, {})
  }, [filteredPatients])

  const facilityGroups = Object.entries(groupedPatients).sort(([facilityA], [facilityB]) =>
    facilityA.localeCompare(facilityB, undefined, { sensitivity: 'base' }),
  )

  const handleUnarchivePatient = async () => {
    const patientId = unarchivePatient?.PatientID

    if (!patientId) {
      toast.error('Patient ID not found', toastConfig)
      return
    }

    setIsUnarchiving(true)

    try {
      const response = await SavePatients('unarchive', '', '', '', patientId, '')

      if (response instanceof Error || response?.isAxiosError) {
        throw response
      }

      setArchivedPatients((patients) => patients.filter((patient) => patient.PatientID !== patientId))
      setUnarchivePatient(null)
      toast.success('Patient Restored Successfully', toastConfig)
    } catch (error) {
      console.error('Failed to restore patient:', error)
      toast.error('Failed to restore patient', toastConfig)
    } finally {
      setIsUnarchiving(false)
    }
  }

  return (
    <div className="position-relative patient-theme-page" data-theme={patientTheme}>
      <Pageloader isLoading={isLoading} />

      <div className="patient-page-wrapper page-wrapper patient-theme-shell dashboard-dark-theme">
        <div className="page-header mb-3">
          <div className="d-flex align-items-center gap-1 patient-theme-toolbar">
            <div className="search-input search-dark dashboard-dark-search">
              <SearchIcon />
              <input
                type="text"
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                placeholder="Search by Patient or Facility..."
              />
            </div>
          </div>

          <div className="d-flex align-items-center">
            <div className="display-total-patient mx-2">
              <span className="mx-1">{filteredPatients.length}</span> Archived Patients
            </div>
          </div>
        </div>

        {!isLoading && loadError && (
          <h6 className="d-flex justify-content-center text-danger">Unable to load archived patients</h6>
        )}

        {!isLoading && !loadError && (
          <div className="hide-drag-table">
            {facilityGroups.map(([facility, patients], facilityIndex) => (
              <div key={facility}>
                <CAccordion alwaysOpen className="dashboard-dark-accordion patient-theme-accordion">
                  <CAccordionItem
                    className="border-0 border-bottom my-2 mx-4 dashboard-dark-facility-item patient-theme-facility-item"
                    itemKey={`archive-facility-${facilityIndex}`}
                  >
                    <CAccordionHeader className="patient-theme-accordion-header" style={{ fontSize: '14px', boxShadow: 'unset' }}>
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <span className="text-primary">
                          {facility} ({patients.length})
                        </span>
                      </div>
                    </CAccordionHeader>

                    <CAccordionBody className="border-bottom-0 border-0 py-0 p-0">
                      <CTable className="mb-0 border-bottom-0 border-0 patient-theme-table">
                        <CTableBody>
                          {patients.map((patient, patientIndex) => (
                            <CTableRow
                              className={`order-0 px-4 ${patient?.Status || ''} patient-row-list`}
                              key={patient?.PatientID || `${facility}-${patientIndex}`}
                              style={{ verticalAlign: 'middle', padding: '20px', top: 0 }}
                            >
                              <CTableHeaderCell className="border-bottom-0 border-0" scope="row" style={{ width: '100%' }}>
                                <div
                                  style={{
                                    display: 'grid',
                                    gridTemplateColumns: '260px minmax(0, 1fr)',
                                    alignItems: 'center',
                                    gap: '16px',
                                    width: '100%',
                                  }}
                                >
                                  <span style={{ paddingLeft: '20px', minWidth: 0 }}>
                                    <span className="patient-name-fixed">{getPatientName(patient) || 'Unnamed Patient'}</span>
                                  </span>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                                    <span className="room-pill" style={{ margin: 0, width: '180px', minWidth: '180px', maxWidth: '180px' }}>
                                      Room No: {patient?.Room || 'N/A'}
                                    </span>
                                    <span className="notestatus-pill" style={{ margin: 0 }}>
                                      Gender: {patient?.Gender || 'N/A'}
                                    </span>
                                    <span className="schedule-pill" style={{ margin: 0 }}>
                                       Age: {patient?.Age ?? 'N/A'}
                                    </span>
                                    <CButton
                                       color="success"
                                       variant="outline"
                                       className="px-2 ms-auto"
                                       aria-label={`Restore ${getPatientName(patient) || 'patient'}`}
                                       onClick={() => setUnarchivePatient(patient)}
                                    >
                                       <CTooltip content="Restore patient">
                                         <ArchiveRestore size={18} />
                                       </CTooltip>
                                    </CButton>
                                  </div>
                                </div>
                              </CTableHeaderCell>
                            </CTableRow>
                          ))}
                        </CTableBody>
                      </CTable>
                    </CAccordionBody>
                  </CAccordionItem>
                </CAccordion>
              </div>
            ))}

            {facilityGroups.length === 0 && (
              <h6 className="d-flex justify-content-center text-secondary">No archived patients found</h6>
            )}
          </div>
        )}

        <CModal
          visible={Boolean(unarchivePatient)}
          onClose={() => !isUnarchiving && setUnarchivePatient(null)}
          backdrop="static"
          className="dashboard-dark-modal patient-theme-modal"
          data-theme={patientTheme}
        >
          <CModalHeader>
            <CModalTitle>Confirm Restore</CModalTitle>
          </CModalHeader>
          <CModalBody>
            Are you sure you want to restore {getPatientName(unarchivePatient) || 'this patient'}?
          </CModalBody>
          <CModalFooter>
            <CButton color="secondary" variant="outline" disabled={isUnarchiving} onClick={() => setUnarchivePatient(null)}>
              Cancel
            </CButton>
            <CButton color="success" disabled={isUnarchiving} onClick={handleUnarchivePatient}>
              {isUnarchiving ? 'Restoring...' : 'Restore'}
            </CButton>
          </CModalFooter>
        </CModal>
      </div>
    </div>
  )
}

export default Archive
